/**
 * Platform tier: who runs Kortex itself, what a tenant is subscribed to, and
 * when that subscription stops granting access.
 */
export const PLATFORM_ROLES = ['NONE', 'SUPPORT', 'SUPERADMIN'];
export const isPlatformAdmin = (role) => role === 'SUPERADMIN' || role === 'SUPPORT';
export const isSuperAdmin = (role) => role === 'SUPERADMIN';
export const SUBSCRIPTION_STATUSES = [
    'TRIALING',
    'ACTIVE',
    'PAST_DUE',
    'SUSPENDED',
    'CANCELLED',
    'EXPIRED',
];
/** Statuses that still let a tenant use the application. */
export const ENTITLED_STATUSES = ['TRIALING', 'ACTIVE', 'PAST_DUE'];
export const SUBSCRIPTION_STATUS_LABELS = {
    TRIALING: 'On trial',
    ACTIVE: 'Active',
    PAST_DUE: 'Payment overdue',
    SUSPENDED: 'Suspended',
    CANCELLED: 'Cancelled',
    EXPIRED: 'Expired',
};
export const DEFAULT_PLAN_CODE = 'FREE';
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Whole days left before `until`, never negative.
 *
 * Counted between calendar days rather than between instants. Rounding the raw
 * hours up left the number stuck: a trial started at 11pm read "30 days" that
 * night and still read 30 the whole of the next day, which is exactly what
 * somebody watching their trial notices and does not believe. Counting dates
 * means it drops by one every midnight, like a calendar does.
 *
 * The day it ends reads zero: they still have that day, and the banner says
 * so in words rather than pretending there is another one after it.
 */
export function daysRemaining(until, now = new Date()) {
    if (!until)
        return 0;
    const end = typeof until === 'string' ? new Date(until) : until;
    if (Number.isNaN(end.getTime()))
        return 0;
    const midnight = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    return Math.max(0, Math.round((midnight(end) - midnight(now)) / DAY_MS));
}
/**
 * The effective status, applying the passage of time.
 *
 * A trial whose end date has passed is EXPIRED whether or not anything has
 * written that to the row yet, so a read and an enforcement check can never
 * disagree.
 */
export function effectiveStatus(subscription, now = new Date()) {
    if (!subscription)
        return 'EXPIRED';
    const { status, trialEndsAt, currentPeriodEnd } = subscription;
    if (status === 'TRIALING') {
        const end = trialEndsAt ? new Date(trialEndsAt) : null;
        return end && end.getTime() <= now.getTime() ? 'EXPIRED' : 'TRIALING';
    }
    if (status === 'ACTIVE' && currentPeriodEnd) {
        const end = new Date(currentPeriodEnd);
        return end.getTime() <= now.getTime() ? 'PAST_DUE' : 'ACTIVE';
    }
    return status;
}
export const isEntitled = (subscription, now = new Date()) => ENTITLED_STATUSES.includes(effectiveStatus(subscription, now));
/** When the subscription stops granting access, for display. */
export const entitlementEndsAt = (subscription) => {
    if (!subscription)
        return null;
    const value = subscription.status === 'TRIALING' ? subscription.trialEndsAt : subscription.currentPeriodEnd;
    return value ? new Date(value) : null;
};
//# sourceMappingURL=platform.js.map